import { useState, useEffect } from 'react';
import { toast, Toaster } from 'sonner';
import { 
  Users, 
  Mail, 
  FileText, 
  LogOut, 
  Trash2, 
  RefreshCw,
  CheckCircle,
  XCircle,
  BarChart3,
  Loader2,
  Eye,
  Sparkles,
  Send,
  Calendar,
  Clock,
  Settings,
  Play,
  Pause
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth Context
const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('admin_token'));

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const storedToken = localStorage.getItem('admin_token');
    if (!storedToken) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` },
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
        setToken(storedToken);
      } else {
        localStorage.removeItem('admin_token');
        setToken(null);
      }
    } catch (err) {
      localStorage.removeItem('admin_token');
      setToken(null);
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
      credentials: 'include'
    });

    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Login failed');
    }

    const data = await res.json();
    localStorage.setItem('admin_token', data.access_token);
    setToken(data.access_token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (e) {
      console.log('Logout cleanup');
    }
    localStorage.removeItem('admin_token');
    setToken(null);
    setUser(null);
  };

  return { user, token, loading, login, logout, checkAuth };
};

// Login Component
const LoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await onLogin(email, password);
      toast.success('Welcome back!');
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-[#2a2a2a] rounded-lg shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#C27C62] to-[#a86a54] px-8 py-6 text-center">
            <h1 className="text-2xl font-light text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Admin Dashboard
            </h1>
            <p className="text-white/70 text-sm mt-1">Redefining Return</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-[#1a1a1a] border border-white/10 rounded text-white placeholder:text-white/30 focus:outline-none focus:border-[#C27C62] transition-colors"
                placeholder="admin@example.com"
                required
                disabled={loading}
                data-testid="admin-email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-[#1a1a1a] border border-white/10 rounded text-white placeholder:text-white/30 focus:outline-none focus:border-[#C27C62] transition-colors"
                placeholder="Enter password"
                required
                disabled={loading}
                data-testid="admin-password-input"
              />
            </div>

            {error && (
              <p className="text-red-400 text-sm" data-testid="login-error">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-[#C27C62] text-white font-medium rounded hover:bg-[#a86a54] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="admin-login-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-white/30 text-xs mt-6">
          Protected admin area
        </p>
      </div>
      <Toaster position="top-right" />
    </div>
  );
};

// Stats Card
const StatCard = ({ icon: Icon, label, value, color = "#C27C62" }) => (
  <div className="bg-[#2a2a2a] rounded-lg p-6 border border-white/5">
    <div className="flex items-center gap-4">
      <div className="p-3 rounded-lg" style={{ backgroundColor: `${color}20` }}>
        <Icon className="w-6 h-6" style={{ color }} />
      </div>
      <div>
        <p className="text-white/50 text-sm">{label}</p>
        <p className="text-2xl font-light text-white">{value}</p>
      </div>
    </div>
  </div>
);

// Subscribers Table
const SubscribersTable = ({ subscribers, onDelete, onRefresh, loading }) => (
  <div className="bg-[#2a2a2a] rounded-lg border border-white/5 overflow-hidden">
    <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
      <h3 className="text-lg font-light text-white flex items-center gap-2">
        <Users className="w-5 h-5 text-[#C27C62]" />
        Newsletter Subscribers
      </h3>
      <button
        onClick={onRefresh}
        disabled={loading}
        className="p-2 text-white/50 hover:text-white transition-colors"
        data-testid="refresh-subscribers-btn"
      >
        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
      </button>
    </div>

    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-white/5">
            <th className="text-left px-6 py-3 text-white/50 text-sm font-medium">Name</th>
            <th className="text-left px-6 py-3 text-white/50 text-sm font-medium">Email</th>
            <th className="text-left px-6 py-3 text-white/50 text-sm font-medium">Subscribed</th>
            <th className="text-left px-6 py-3 text-white/50 text-sm font-medium">Status</th>
            <th className="text-right px-6 py-3 text-white/50 text-sm font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {subscribers.length === 0 ? (
            <tr>
              <td colSpan="5" className="px-6 py-8 text-center text-white/30">
                No subscribers yet
              </td>
            </tr>
          ) : (
            subscribers.map((sub) => (
              <tr key={sub.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                <td className="px-6 py-4 text-white">{sub.first_name}</td>
                <td className="px-6 py-4 text-white/70">{sub.email}</td>
                <td className="px-6 py-4 text-white/50 text-sm">
                  {new Date(sub.subscribed_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${
                    sub.status === 'active' 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-red-500/20 text-red-400'
                  }`}>
                    {sub.status === 'active' ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                    {sub.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-right">
                  <button
                    onClick={() => onDelete(sub.id)}
                    className="p-2 text-red-400/50 hover:text-red-400 transition-colors"
                    data-testid={`delete-subscriber-${sub.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

// Newsletters Table
const NewslettersTable = ({ newsletters, loading, onSend, sending }) => (
  <div className="bg-[#2a2a2a] rounded-lg border border-white/5 overflow-hidden">
    <div className="px-6 py-4 border-b border-white/5">
      <h3 className="text-lg font-light text-white flex items-center gap-2">
        <Mail className="w-5 h-5 text-[#94A48A]" />
        Generated Newsletters
      </h3>
    </div>

    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-white/5">
            <th className="text-left px-6 py-3 text-white/50 text-sm font-medium">Subject</th>
            <th className="text-left px-6 py-3 text-white/50 text-sm font-medium">Topic</th>
            <th className="text-left px-6 py-3 text-white/50 text-sm font-medium">Created</th>
            <th className="text-right px-6 py-3 text-white/50 text-sm font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {newsletters.length === 0 ? (
            <tr>
              <td colSpan="4" className="px-6 py-8 text-center text-white/30">
                No newsletters generated yet
              </td>
            </tr>
          ) : (
            newsletters.map((nl) => (
              <tr key={nl.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                <td className="px-6 py-4 text-white max-w-xs truncate">{nl.subject}</td>
                <td className="px-6 py-4 text-white/70">{nl.topic}</td>
                <td className="px-6 py-4 text-white/50 text-sm">
                  {new Date(nl.created_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 text-right flex items-center justify-end gap-2">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(nl.content);
                      toast.success('Newsletter content copied!');
                    }}
                    className="p-2 text-white/50 hover:text-white transition-colors"
                    title="Copy content"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onSend(nl.id)}
                    disabled={sending}
                    className="p-2 text-[#94A48A]/70 hover:text-[#94A48A] transition-colors disabled:opacity-50"
                    title="Send to all subscribers"
                    data-testid={`send-newsletter-${nl.id}`}
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

// Main Dashboard
const Dashboard = ({ user, token, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [topic, setTopic] = useState('');
  const [schedule, setSchedule] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadData();
    loadSchedule();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, subsRes, nlRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/admin/subscribers`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/admin/newsletter/history`, { headers, credentials: 'include' })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (subsRes.ok) {
        const data = await subsRes.json();
        setSubscribers(data.subscribers || []);
      }
      if (nlRes.ok) setNewsletters(await nlRes.json());
    } catch (err) {
      toast.error('Failed to load data');
    }
    setLoading(false);
  };

  const loadSchedule = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/newsletter/schedule`, { headers, credentials: 'include' });
      if (res.ok) setSchedule(await res.json());
    } catch (err) {
      console.error('Failed to load schedule');
    }
  };

  const handleDeleteSubscriber = async (id) => {
    if (!window.confirm('Are you sure you want to delete this subscriber?')) return;

    try {
      const res = await fetch(`${API_URL}/api/admin/subscribers/${id}`, {
        method: 'DELETE',
        headers,
        credentials: 'include'
      });
      if (res.ok) {
        setSubscribers(subscribers.filter(s => s.id !== id));
        toast.success('Subscriber deleted');
      } else {
        toast.error('Failed to delete subscriber');
      }
    } catch (err) {
      toast.error('Failed to delete subscriber');
    }
  };

  const handleSendNewsletter = async (newsletterId) => {
    if (!window.confirm(`Send this newsletter to all ${subscribers.length} subscribers?`)) return;

    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/newsletter/send`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ newsletter_id: newsletterId })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Newsletter sent to ${data.sent} subscribers!`);
        loadData(); // Refresh stats
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to send newsletter');
      }
    } catch (err) {
      toast.error('Failed to send newsletter');
    }
    setSending(false);
  };

  const handleToggleSchedule = async () => {
    const newEnabled = !schedule?.enabled;
    try {
      const res = await fetch(`${API_URL}/api/admin/newsletter/schedule`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          enabled: newEnabled,
          frequency: schedule?.frequency || 'biweekly',
          day_of_week: schedule?.day_of_week || 0,
          hour: schedule?.hour || 9,
          auto_generate_topic: schedule?.auto_generate_topic || 'leadership and career development'
        })
      });

      if (res.ok) {
        const data = await res.json();
        setSchedule(data);
        toast.success(newEnabled ? 'Bi-weekly newsletters enabled!' : 'Scheduled newsletters disabled');
      }
    } catch (err) {
      toast.error('Failed to update schedule');
    }
  };

  const handleGenerateNewsletter = async () => {
    if (!topic.trim()) {
      toast.error('Please enter a topic');
      return;
    }

    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/newsletter/generate`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          topic: topic.trim(),
          audience: "executive leaders and senior professionals",
          tone: "professional yet warm and approachable",
          length: "medium",
          include_cta: true
        })
      });

      if (res.ok) {
        const data = await res.json();
        setNewsletters([data, ...newsletters]);
        setTopic('');
        toast.success('Newsletter generated!');
      } else {
        toast.error('Failed to generate newsletter');
      }
    } catch (err) {
      toast.error('Failed to generate newsletter');
    }
    setGenerating(false);
  };

  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      {/* Header */}
      <header className="bg-[#2a2a2a] border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-light text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Admin Dashboard
            </h1>
            <p className="text-white/50 text-sm">Welcome, {user?.name || 'Admin'}</p>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="/"
              className="text-white/50 hover:text-white text-sm transition-colors"
            >
              View Site
            </a>
            <button
              onClick={onLogout}
              className="flex items-center gap-2 px-4 py-2 bg-white/5 text-white/70 rounded hover:bg-white/10 transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={Users}
            label="Total Subscribers"
            value={stats?.total_subscribers || 0}
            color="#C27C62"
          />
          <StatCard
            icon={BarChart3}
            label="This Week"
            value={stats?.recent_subscribers || 0}
            color="#94A48A"
          />
          <StatCard
            icon={Mail}
            label="Newsletters"
            value={stats?.total_newsletters || 0}
            color="#5A6F8C"
          />
          <StatCard
            icon={FileText}
            label="Files"
            value={stats?.total_files || 0}
            color="#D4B06C"
          />
        </div>

        {/* Google Sheets Status */}
        <div className="mb-8 flex items-center gap-2 text-sm">
          {stats?.google_sheets_connected ? (
            <>
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span className="text-green-400">Google Sheets Connected</span>
            </>
          ) : (
            <>
              <XCircle className="w-4 h-4 text-yellow-400" />
              <span className="text-yellow-400">Google Sheets Not Connected</span>
            </>
          )}
        </div>

        {/* Newsletter Schedule */}
        <div className="bg-[#2a2a2a] rounded-lg border border-white/5 p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-light text-white flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#5A6F8C]" />
              Bi-Weekly Newsletter Schedule
            </h3>
            <button
              onClick={handleToggleSchedule}
              className={`flex items-center gap-2 px-4 py-2 rounded transition-colors ${
                schedule?.enabled 
                  ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                  : 'bg-white/5 text-white/50 hover:bg-white/10'
              }`}
              data-testid="toggle-schedule-btn"
            >
              {schedule?.enabled ? (
                <>
                  <Pause className="w-4 h-4" />
                  Enabled
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Disabled
                </>
              )}
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center gap-2 text-white/50">
              <Clock className="w-4 h-4" />
              <span>Every other Monday at 9 AM UTC</span>
            </div>
            <div className="flex items-center gap-2 text-white/50">
              <Mail className="w-4 h-4" />
              <span>Auto-generates & sends to all subscribers</span>
            </div>
            {schedule?.next_send && schedule?.enabled && (
              <div className="flex items-center gap-2 text-[#94A48A]">
                <Calendar className="w-4 h-4" />
                <span>Next: {new Date(schedule.next_send).toLocaleDateString()}</span>
              </div>
            )}
          </div>
          
          {stats?.newsletter_schedule?.last_sent && (
            <div className="mt-4 pt-4 border-t border-white/5 text-sm text-white/30">
              Last sent: {new Date(stats.newsletter_schedule.last_sent).toLocaleDateString()} 
              ({stats.newsletter_schedule.last_sent_count} recipients)
            </div>
          )}
        </div>

        {/* Newsletter Generator */}
        <div className="bg-[#2a2a2a] rounded-lg border border-white/5 p-6 mb-8">
          <h3 className="text-lg font-light text-white flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-[#D4B06C]" />
            Generate Newsletter with AI
          </h3>
          <div className="flex gap-4">
            <input
              type="text"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="Enter newsletter topic (e.g., 'Leadership in times of change')"
              className="flex-1 px-4 py-3 bg-[#1a1a1a] border border-white/10 rounded text-white placeholder:text-white/30 focus:outline-none focus:border-[#D4B06C] transition-colors"
              disabled={generating}
              data-testid="newsletter-topic-input"
            />
            <button
              onClick={handleGenerateNewsletter}
              disabled={generating || !topic.trim()}
              className="px-6 py-3 bg-[#D4B06C] text-black font-medium rounded hover:bg-[#c4a05c] transition-colors disabled:opacity-50 flex items-center gap-2"
              data-testid="generate-newsletter-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Generate
                </>
              )}
            </button>
          </div>
        </div>

        {/* Tables */}
        <div className="space-y-8">
          <SubscribersTable
            subscribers={subscribers}
            onDelete={handleDeleteSubscriber}
            onRefresh={loadData}
            loading={loading}
          />
          <NewslettersTable 
            newsletters={newsletters} 
            loading={loading} 
            onSend={handleSendNewsletter}
            sending={sending}
          />
        </div>
      </main>

      <Toaster position="top-right" />
    </div>
  );
};

// Main Component
export default function AdminDashboard() {
  const { user, token, loading, login, logout } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C27C62] animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <LoginForm onLogin={login} />;
  }

  return <Dashboard user={user} token={token} onLogout={logout} />;
}
